import { useGetMe } from 'api-hooks/auth/query';
import { ActivitySaleModel } from 'api-hooks/sale/activity';
import { generateId } from 'common/utils/string';
import {
  guestNameTransformer,
  preferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  ActivitySaleAddOnsItemFormType,
  ActivitySaleFormType,
  ActivitySaleGuestItemFormType,
  ActivitySaleItemFormType,
  ActivitySalesGuestDetailFormType,
} from '../form-type';

interface Props {
  activitySale?: ActivitySaleModel;
  defaultValues?: ActivitySaleFormType;
}

export function addNewAddOnsItem(): ActivitySaleAddOnsItemFormType {
  return {
    description: '',
    guestId: '',
    guestName: '',
    id: undefined,
    price: undefined,
    customId: generateId(),
  } as never as ActivitySaleAddOnsItemFormType;
}

export function addNewGuestDetailItem(): ActivitySalesGuestDetailFormType {
  return {
    activityId: '',
    activityName: '',
    activityCode: '',
    customId: generateId(),
    description: '',
    id: undefined,
    price: undefined,
    qty: undefined,
    totalPrice: undefined,
    type: '',
    date: undefined,
  } as never as ActivitySalesGuestDetailFormType;
}

export function addNewGuestItem(): ActivitySaleGuestItemFormType {
  return {
    activitySaleGuestDetails: [addNewGuestDetailItem()],
    customId: generateId(),
    guestId: '',
    guestName: '',
    id: undefined,
    preferences: [],
  } as never as ActivitySaleGuestItemFormType;
}

export function addNewActivitySaleItem(): ActivitySaleItemFormType {
  return {
    activitySaleAddOns: [],
    activitySaleGuests: [addNewGuestItem()],
    customId: generateId(),
    id: undefined,
    isGroupedPrice: false,
  } as never as ActivitySaleItemFormType;
}

export default function useGetDefaultValues(props: Props) {
  const { activitySale } = props;
  const { data: employeeData } = useGetMe();
  const employeeHook = employeeData?.data;

  const defaultValues = React.useMemo<ActivitySaleFormType>(() => {
    const _defaultValues = props.defaultValues;
    const transactionAt = activitySale?.transactionAt ?? new Date();

    if (_defaultValues) {
      return {
        ..._defaultValues,
        transactionAt,
        tempTransactionAt: transactionAt,
        activitySales: _defaultValues.activitySales.map((as) => {
          return {
            ...as,
            id: undefined,

            activitySaleAddOns: as.activitySaleAddOns.map((asao) => {
              return {
                ...asao,
                id: undefined,
              };
            }),
            activitySaleGuests: as.activitySaleGuests.map((asg) => {
              return {
                ...asg,
                activitySaleGuestDetails: asg.activitySaleGuestDetails.map(
                  (asgd) => {
                    return {
                      ...asgd,
                      id: undefined,
                    };
                  },
                ),
                id: undefined,
              };
            }),
          };
        }),
        refId: undefined,
        activitySale: undefined,
      };
    }
    const customer = activitySale?.customer;

    return {
      address: customer?.address || '',
      adminId: activitySale?.admin?.id || employeeHook?.id || '',
      code: activitySale?.code || '',
      customerBillingId: activitySale?.customerBillingAddress?.id || '',
      customerBillingOptions: [],
      customerId: customer?.id || '',
      customerStatus: customer?.status || '',
      description: activitySale?.description || '',
      discount: activitySale?.discount || 0,
      hasDueReceivable: customer?.hasDueReceivables || false,
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      needApproval: false,
      tempTransactionAt: transactionAt,
      number: activitySale?.invoiceNumber || '',
      operatorId: activitySale?.operator?.id || '',
      otherCost: activitySale?.otherCost || 0,
      phone: customer?.phone ?? '',
      refId: activitySale?.refId,
      parentRefId: activitySale?.parentSale?.refId,
      salesmanId: activitySale?.salesman?.id ?? '',
      total: activitySale?.total || 0,
      activitySales: activitySale?.activitySales?.map((as) => {
        const activitySaleAddOns: ActivitySaleAddOnsItemFormType[] =
          as.activitySaleAddOns.map((asao) => {
            const guest = asao.guest;
            return {
              description: asao.description || '',
              guestId: guest.id || '',
              guestName: guestNameTransformer(guest),
              id: asao.id,
              price: asao.price,
              customId: generateId(),
            };
          });

        const activitySaleGuests: ActivitySaleGuestItemFormType[] =
          as.activitySaleGuests.map((asg) => {
            const guest = asg?.guest;
            const activitySaleGuestDetails: ActivitySalesGuestDetailFormType[] =
              asg.activitySaleGuestDetails.map((asgd) => {
                const activity = asgd.activity;
                return {
                  activityId: activity.id,
                  activityName: activity.name,
                  activityCode: activity.code,
                  customId: generateId(),
                  description: asgd.description || '',
                  id: asgd.id,
                  price: asgd.price || 0,
                  qty: asgd.qty,
                  totalPrice: asgd.totalPrice,
                  type: activity.type,
                  date: asgd.date,
                };
              });

            return {
              customId: generateId(),
              guestId: guest.id,
              guestName: guestNameTransformer(guest),
              id: asg.id,
              preferences: preferencesTransformer(asg.preferences),
              activitySaleGuestDetails,
            };
          });

        return {
          activitySaleAddOns,
          activitySaleGuests,
          id: as.id,
          isGroupedPrice: as.isGroupedPrice,
          customId: generateId(),
        };
      }) ?? [addNewActivitySaleItem()],
      transactionAt,
      activitySale,
    };
  }, [activitySale, employeeHook?.id, props.defaultValues]);

  return defaultValues;
}
